"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import { useAppConfig } from "@/lib/config";

/**
 * GlobalCreditSync connects to the `/user/events` SSE stream.
 * When the backend worker deducts credits and publishes a Redis event,
 * this component receives it and invalidates the orgs cache so the
 * sidebar credits number updates without a manual page refresh.
 *
 * This is mounted once at the top level (inside Providers) and is
 * completely independent of any chat logic.
 */
export function GlobalCreditSync() {
	const queryClient = useQueryClient();
	const router = useRouter();
	const config = useAppConfig();
	const esRef = useRef<EventSource | null>(null);
	const retryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const connect = () => {
			if (esRef.current) {
				esRef.current.close();
			}

			const sseUrl = `${config.apiUrl}/user/events`;
			const es = new EventSource(sseUrl, { withCredentials: true });
			esRef.current = es;

			const handleCreditUpdate = () => {
				// Invalidate the orgs query so the credits re-fetch from the server
				void queryClient.invalidateQueries({ queryKey: ["get", "/orgs"] });
				void queryClient.refetchQueries({ queryKey: ["get", "/orgs"] });
				router.refresh();
			};

			es.addEventListener("credits_updated", handleCreditUpdate);
			es.addEventListener("update", handleCreditUpdate);

			es.onerror = () => {
				es.close();
				esRef.current = null;
				// Reconnect after 5 seconds
				retryTimeout.current = setTimeout(connect, 5000);
			};
		};

		connect();

		return () => {
			esRef.current?.close();
			esRef.current = null;
			if (retryTimeout.current) {
				clearTimeout(retryTimeout.current);
			}
		};
	}, [config.apiUrl, queryClient, router]);

	return null;
}
